import { Chip, cn, Input, Spinner, Tab, Tabs } from '@nextui-org/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Typography } from '~/components/common/Typography'
import { Modal } from '~/components/modals/CurieModal'
import useConversationsList from '~/hooks/super-admin/useConversationsList'
import useTasksList from '~/hooks/super-admin/useTasksList'
import { CommandType, commandTypeFromJSON } from '~/protos/agents'
import {
  AdminUsageSummary,
  BillingLookupResponse,
  BrowseAccount,
  BrowseDomain,
  BrowseEmail,
  getAdminUsageSummary,
  getBillingLookup,
  getBrowseData,
  getTaskNavigation,
  getTasksByConversationUuid,
  getUserTasksByEmail,
  getWorkspaceLookup,
  WorkspaceLookupResponse
} from '~/services/superAdminService'
import { generate_task_title, getConversationDisplayTitle } from '~/utils/taskTitles'
import { BetterToast } from '~/utils/toast'
import { SuperAdminConversationDisplay } from './SuperAdminConversationDisplay'
import { SuperAdminTaskDisplay } from './SuperAdminTaskDisplay'

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
  ERRORED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Errored' },
  IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'In progress' },
  RUNNING: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Running' },
  SUBMITTED: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Submitted' },
  ARCHIVED: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Archived' },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Cancelled' },
  STOPPED: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Stopped' },
  DIFF_GENERATED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Diff gen.' },
  DOCUMENT_GENERATED: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Doc gen.' },
  MIGRATION_DOCUMENT_GENERATED: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Doc gen.' },
  DRAFT_GENERATED: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Draft gen.' },
  MUNIT_DIFF_GENERATED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'MUnit gen.' },
  INTEGRATION_TEST_DIFF_GENERATED: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    label: 'Test gen.'
  },
  MIGRATION_REFACTOR_ACCELERATOR_DIFF_GENERATED: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    label: 'Refactor gen.'
  },
  PLANNING_COMPLETED: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Planned' },
  PLANNING_UPDATED: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Plan updated' },
  PLANNING_REREQUESTED: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Plan review' },
  STEPS_GENERATED: { bg: 'bg-violet-100', text: 'text-violet-700', label: 'Steps gen.' },
  STEPS_UPDATED: { bg: 'bg-violet-100', text: 'text-violet-700', label: 'Steps updated' },
  AGENT_INTERNAL_STATUS: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Processing' }
}

function StatusBadge({
  status,
  className,
  title
}: {
  status: string
  className?: string
  /** Defaults to full status label for hover when truncated */
  title?: string
}) {
  const colors = STATUS_COLORS[status] || {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    label: status
  }
  return (
    <span
      title={title ?? colors.label}
      className={cn(
        'inline-block max-w-full truncate px-2 py-0.5 text-xs font-medium rounded-full',
        colors.bg,
        colors.text,
        className
      )}
    >
      {colors.label}
    </span>
  )
}

interface UserTask {
  task_id: number
  cmd_type: string
  status: string
  created_at: string | null
  updated_at: string | null
}

// Format date
const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// ============================================================================
// Find Tab - Clear labeled search fields
// ============================================================================
const FindTab: React.FC<{
  onTaskSelect: (taskId: number, workspaceUuid: string) => void
  onViewConversation: (conversationId: number, title: string) => void
  searchParams: URLSearchParams
  setSearchParams: ReturnType<typeof useSearchParams>[1]
}> = ({ onTaskSelect, onViewConversation, searchParams, setSearchParams }) => {
  // Input states - sync with URL
  const [taskIdInput, setTaskIdInput] = useState('')
  const [conversationIdInput, setConversationIdInput] = useState('')
  const [emailInput, setEmailInput] = useState(() => searchParams.get('findEmail') || '')

  // Loading states
  const [loadingTask, setLoadingTask] = useState(false)
  const [loadingConversation, setLoadingConversation] = useState(false)
  const [loadingEmail, setLoadingEmail] = useState(false)
  const [loadingWorkspace, setLoadingWorkspace] = useState(false)
  const [loadingBilling, setLoadingBilling] = useState(false)

  // Result states - only email results stay on page (task & conversation open modals directly)
  const [emailResult, setEmailResult] = useState<{
    email: string
    user_id?: number
    userUsage?: AdminUsageSummary
    tasks: UserTask[]
    conversations: Array<{
      id: number
      uuid: string
      title: string
      mode: string | null
      created_at: string | null
      message_count: number
    }>
  } | null>(null)

  const [workspaceInput, setWorkspaceInput] = useState('')
  const [billingInput, setBillingInput] = useState('')
  const [workspaceExplorer, setWorkspaceExplorer] = useState<{
    lookup: WorkspaceLookupResponse
    usage: AdminUsageSummary
  } | null>(null)
  const [billingExplorer, setBillingExplorer] = useState<{
    lookup: BillingLookupResponse
    billingUsage: AdminUsageSummary
    byOrg: Record<number, AdminUsageSummary>
  } | null>(null)
  const [billingDrillOrgId, setBillingDrillOrgId] = useState<number | null>(null)
  const [billingDrillUuid, setBillingDrillUuid] = useState<string>('')

  const clearFindPanels = useCallback(() => {
    setEmailResult(null)
    setWorkspaceExplorer(null)
    setBillingExplorer(null)
    setBillingDrillOrgId(null)
    setBillingDrillUuid('')
  }, [])

  // Status filter - sync with URL
  const [statusFilter, setStatusFilter] = useState<string | null>(
    () => searchParams.get('status') || null
  )

  // View mode for email results - sync with URL
  const [emailViewMode, setEmailViewMode] = useState<'tasks' | 'conversations'>(() => {
    const view = searchParams.get('view')
    return view === 'conversations' ? 'conversations' : 'tasks'
  })

  // Sync state to URL query params
  useEffect(() => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev)
        if (emailInput) params.set('findEmail', emailInput)
        else params.delete('findEmail')
        if (emailViewMode !== 'tasks') params.set('view', emailViewMode)
        else params.delete('view')
        if (statusFilter) params.set('status', statusFilter)
        else params.delete('status')
        return params
      },
      { replace: true }
    )
  }, [emailInput, emailViewMode, statusFilter, setSearchParams])

  // Find by Task ID - opens task modal directly. Accepts an optional id so
  // we can drive the same flow from a URL param (`?findTaskId=…`) without
  // first stuffing the value into the input.
  const handleFindTask = async (taskIdOverride?: number) => {
    const id = taskIdOverride !== undefined ? taskIdOverride : parseInt(taskIdInput.trim())
    if (isNaN(id)) {
      BetterToast.error('Please enter a valid Task ID')
      return
    }

    setLoadingTask(true)
    clearFindPanels()

    try {
      const navInfo = await getTaskNavigation(id)
      if (navInfo.task_deleted) {
        BetterToast.error(`Task ${id} has been deleted`)
        return
      }
      onTaskSelect(navInfo.task_id, navInfo.workspace_uuid ?? '')
    } catch {
      BetterToast.error(`Task ID ${id} not found`)
    } finally {
      setLoadingTask(false)
    }
  }

  // Auto-trigger task lookup when the URL carries `findTaskId` (e.g. from
  // a deep link in the API Keys tab). We then strip the param so refreshing
  // the page doesn't re-open the modal.
  const findTaskIdParam = searchParams.get('findTaskId')
  useEffect(() => {
    if (!findTaskIdParam) return
    const id = parseInt(findTaskIdParam)
    if (isNaN(id)) return
    setTaskIdInput(findTaskIdParam)
    void handleFindTask(id)
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev)
        params.delete('findTaskId')
        return params
      },
      { replace: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findTaskIdParam])

  // Check if input is UUID format
  const isUuid = (value: string) => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim())
  }

  // Find by Conversation ID or UUID - opens conversation messages directly
  const handleFindConversation = async () => {
    const input = conversationIdInput.trim()
    if (!input) {
      BetterToast.error('Please enter a Conversation ID or UUID')
      return
    }

    setLoadingConversation(true)
    clearFindPanels()

    try {
      let conversationId: number

      if (isUuid(input)) {
        // Search by UUID - get conversation ID first
        const response = await getTasksByConversationUuid(input)
        conversationId = response.conversation_id
      } else {
        // Search by numeric ID
        const id = parseInt(input)
        if (isNaN(id)) {
          BetterToast.error('Please enter a valid Conversation ID or UUID')
          setLoadingConversation(false)
          return
        }
        conversationId = id
      }

      // Open conversation messages directly - no intermediate step
      onViewConversation(conversationId, `Conversation #${conversationId}`)
    } catch {
      BetterToast.error(`Conversation not found: ${input}`)
    } finally {
      setLoadingConversation(false)
    }
  }

  // Find by Email
  const handleFindEmail = async () => {
    const email = emailInput.trim()
    if (!email || !email.includes('@') || !email.includes('.')) {
      BetterToast.error('Please enter a valid email address')
      return
    }

    setLoadingEmail(true)
    clearFindPanels()
    setStatusFilter(null)

    try {
      const result = await getUserTasksByEmail(email)
      let userUsage: AdminUsageSummary | undefined
      if (result.user_id != null) {
        try {
          userUsage = await getAdminUsageSummary({ scope: 'user', userId: result.user_id })
        } catch {
          userUsage = undefined
        }
      }
      setEmailResult({
        email,
        user_id: result.user_id,
        userUsage,
        tasks: result.tasks,
        conversations: result.conversations
      })
    } catch {
      setEmailResult({ email, tasks: [], conversations: [] })
    } finally {
      setLoadingEmail(false)
    }
  }

  const handleFindWorkspace = async () => {
    const q = workspaceInput.trim()
    if (!q) {
      BetterToast.error('Enter workspace id or UUID')
      return
    }
    setLoadingWorkspace(true)
    clearFindPanels()
    try {
      const lookup = await getWorkspaceLookup(q)
      const usage = await getAdminUsageSummary({ scope: 'workspace', orgId: lookup.org_id })
      setWorkspaceExplorer({ lookup, usage })
    } catch {
      BetterToast.error('Workspace not found')
    } finally {
      setLoadingWorkspace(false)
    }
  }

  const handleFindBilling = async () => {
    const raw = billingInput.trim()
    const id = parseInt(raw, 10)
    if (isNaN(id) || id < 1) {
      BetterToast.error('Enter a valid billing account id')
      return
    }
    setLoadingBilling(true)
    clearFindPanels()
    setBillingDrillOrgId(null)
    setBillingDrillUuid('')
    try {
      const lookup = await getBillingLookup(id)
      const billingUsage = await getAdminUsageSummary({ scope: 'billing', billingId: id })
      const byOrg: Record<number, AdminUsageSummary> = {}
      await Promise.all(
        lookup.workspaces.map(async ws => {
          try {
            byOrg[ws.org_id] = await getAdminUsageSummary({
              scope: 'workspace',
              orgId: ws.org_id
            })
          } catch {
            /* skip */
          }
        })
      )
      setBillingExplorer({ lookup, billingUsage, byOrg })
    } catch {
      BetterToast.error('Billing account not found')
    } finally {
      setLoadingBilling(false)
    }
  }

  // Handle task click from email results
  const handleTaskClick = async (taskId: number) => {
    try {
      const navInfo = await getTaskNavigation(taskId)
      if (navInfo.task_deleted) {
        BetterToast.error(`Task ${taskId} has been deleted`)
        return
      }
      onTaskSelect(navInfo.task_id, navInfo.workspace_uuid ?? '')
    } catch {
      BetterToast.error('Failed to load task')
    }
  }

  // Get current tasks for filtering (only email results now)
  const currentTasks = useMemo(() => {
    return emailResult?.tasks || []
  }, [emailResult?.tasks])

  const filteredTasks = useMemo(() => {
    if (!statusFilter) return currentTasks
    return currentTasks.filter(t => t.status === statusFilter)
  }, [currentTasks, statusFilter])

  const availableStatuses = useMemo(() => {
    return Array.from(new Set(currentTasks.map(t => t.status)))
  }, [currentTasks])

  return (
    <div className='flex flex-col h-full'>
      {/* Search Fields */}
      <div className='shrink-0 p-6 border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1'>
        <div className='max-w-[1920px] mx-auto w-full'>
          <h2 className='text-lg font-semibold text-tokens-neutral-text-primary mb-4'>
            Find tasks, conversations, workspaces, and billing
          </h2>

          {/*
            Priority (left → right): billing first, then workspace (usage / drill-down), then task &
            conversation (modals), then user email. Grid gives equal column width on wide viewports.
          */}
          <div className='grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5'>
            {/* 1 — Billing */}
            <div className='flex min-w-0 flex-col gap-2'>
              <label className='whitespace-nowrap text-sm font-medium text-tokens-neutral-text-secondary'>
                Billing ID
              </label>
              <div className='flex min-w-0 gap-2'>
                <Input
                  type='number'
                  placeholder='e.g. 7'
                  value={billingInput}
                  onChange={e => setBillingInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleFindBilling()}
                  classNames={{
                    base: 'min-w-0 flex-1',
                    inputWrapper: 'h-10 bg-white'
                  }}
                />
                <button
                  type='button'
                  onClick={handleFindBilling}
                  disabled={!billingInput.trim() || loadingBilling}
                  className='h-10 shrink-0 rounded-lg bg-tokens-primary-2 px-3 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4'
                >
                  {loadingBilling ? <Spinner size='sm' color='white' /> : 'Find'}
                </button>
              </div>
            </div>

            {/* 2 — Workspace (org id / uuid) */}
            <div className='flex min-w-0 flex-col gap-2'>
              <label className='whitespace-nowrap text-sm font-medium text-tokens-neutral-text-secondary'>
                Workspace
              </label>
              <div className='flex min-w-0 gap-2'>
                <Input
                  type='text'
                  placeholder='e.g. 42 or uuid'
                  value={workspaceInput}
                  onChange={e => setWorkspaceInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleFindWorkspace()}
                  classNames={{
                    base: 'min-w-0 flex-1',
                    inputWrapper: 'h-10 bg-white'
                  }}
                />
                <button
                  type='button'
                  onClick={handleFindWorkspace}
                  disabled={!workspaceInput.trim() || loadingWorkspace}
                  className='h-10 shrink-0 rounded-lg bg-tokens-primary-2 px-3 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4'
                >
                  {loadingWorkspace ? <Spinner size='sm' color='white' /> : 'Find'}
                </button>
              </div>
            </div>

            {/* 3 — Task ID */}
            <div className='flex min-w-0 flex-col gap-2'>
              <label className='whitespace-nowrap text-sm font-medium text-tokens-neutral-text-secondary'>
                Task ID
              </label>
              <div className='flex min-w-0 gap-2'>
                <Input
                  type='number'
                  placeholder='e.g. 12345'
                  value={taskIdInput}
                  onChange={e => setTaskIdInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleFindTask()}
                  classNames={{
                    base: 'min-w-0 flex-1',
                    inputWrapper: 'h-10 bg-white'
                  }}
                />
                <button
                  type='button'
                  onClick={() => handleFindTask()}
                  disabled={!taskIdInput.trim() || loadingTask}
                  className='h-10 shrink-0 rounded-lg bg-tokens-primary-2 px-3 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4'
                >
                  {loadingTask ? <Spinner size='sm' color='white' /> : 'Find'}
                </button>
              </div>
            </div>

            {/* 4 — Conversation ID / UUID */}
            <div className='flex min-w-0 flex-col gap-2'>
              <label className='whitespace-nowrap text-sm font-medium text-tokens-neutral-text-secondary'>
                Conversation
              </label>
              <div className='flex min-w-0 gap-2'>
                <Input
                  type='text'
                  placeholder='ID or UUID'
                  value={conversationIdInput}
                  onChange={e => setConversationIdInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleFindConversation()}
                  classNames={{
                    base: 'min-w-0 flex-1',
                    inputWrapper: 'h-10 bg-white'
                  }}
                />
                <button
                  type='button'
                  onClick={handleFindConversation}
                  disabled={!conversationIdInput.trim() || loadingConversation}
                  className='h-10 shrink-0 rounded-lg bg-tokens-primary-2 px-3 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4'
                >
                  {loadingConversation ? <Spinner size='sm' color='white' /> : 'Find'}
                </button>
              </div>
            </div>

            {/* 5 — User email */}
            <div className='flex min-w-0 flex-col gap-2'>
              <label className='whitespace-nowrap text-sm font-medium text-tokens-neutral-text-secondary'>
                User email
              </label>
              <div className='flex min-w-0 gap-2'>
                <Input
                  type='email'
                  placeholder='e.g. user@company.com'
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleFindEmail()}
                  classNames={{
                    base: 'min-w-0 flex-1',
                    inputWrapper: 'h-10 bg-white'
                  }}
                />
                <button
                  type='button'
                  onClick={handleFindEmail}
                  disabled={!emailInput.trim() || loadingEmail}
                  className='h-10 shrink-0 rounded-lg bg-tokens-primary-2 px-3 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4'
                >
                  {loadingEmail ? <Spinner size='sm' color='white' /> : 'Find'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Area - Only Email results shown here (Task ID and Conversation open modals directly) */}
      <div className='flex-1 overflow-auto p-6'>
        <div className='max-w-6xl mx-auto space-y-8'>
          {/* Email Result */}
          {emailResult && (
            <div className='space-y-4'>
              {/* Header with view toggle */}
              <div className='flex items-center justify-between'>
                <h3 className='text-lg font-semibold'>
                  Results for <span className='text-tokens-primary-2'>{emailResult.email}</span>
                </h3>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => setEmailViewMode('tasks')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      emailViewMode === 'tasks'
                        ? 'bg-tokens-primary-2 text-white'
                        : 'bg-tokens-neutral-bg-grey-1 text-tokens-neutral-text-secondary hover:bg-tokens-neutral-bg-grey-2'
                    }`}
                  >
                    Tasks ({emailResult.tasks.length})
                  </button>
                  <button
                    onClick={() => setEmailViewMode('conversations')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      emailViewMode === 'conversations'
                        ? 'bg-tokens-primary-2 text-white'
                        : 'bg-tokens-neutral-bg-grey-1 text-tokens-neutral-text-secondary hover:bg-tokens-neutral-bg-grey-2'
                    }`}
                  >
                    Conversations ({emailResult.conversations.length})
                  </button>
                </div>
              </div>

              {emailResult.userUsage && (
                <UsageStatRow title='User (all workspaces)' usage={emailResult.userUsage} />
              )}

              {/* Tasks View */}
              {emailViewMode === 'tasks' && (
                <>
                  {/* Status Filters */}
                  {availableStatuses.length > 1 && (
                    <div className='flex items-center gap-2 flex-wrap'>
                      <span className='text-xs font-medium text-tokens-neutral-text-secondary'>
                        Filter:
                      </span>
                      <Chip
                        size='sm'
                        variant={statusFilter === null ? 'solid' : 'bordered'}
                        className='cursor-pointer'
                        onClick={() => setStatusFilter(null)}
                      >
                        All
                      </Chip>
                      {availableStatuses.map(status => (
                        <Chip
                          key={status}
                          size='sm'
                          variant={statusFilter === status ? 'solid' : 'bordered'}
                          className='cursor-pointer'
                          onClick={() => setStatusFilter(statusFilter === status ? null : status)}
                        >
                          {STATUS_COLORS[status]?.label || status}
                        </Chip>
                      ))}
                    </div>
                  )}

                  {/* Tasks List */}
                  {filteredTasks.length === 0 ? (
                    <div className='p-8 text-center text-tokens-neutral-text-tertiary bg-tokens-neutral-bg-grey-1 rounded-lg'>
                      No tasks found for this user
                    </div>
                  ) : (
                    <div className='space-y-2'>
                      {filteredTasks.map(task => {
                        const t = task as UserTask
                        return (
                          <div
                            key={t.task_id}
                            onClick={() => handleTaskClick(t.task_id)}
                            className='p-4 bg-white border border-tokens-neutral-border-2 rounded-lg hover:border-tokens-primary-3 cursor-pointer transition-all'
                          >
                            <div className='flex items-center justify-between'>
                              <div className='flex items-center gap-3'>
                                <span className='font-mono text-sm text-tokens-primary-2 bg-tokens-primary-3/30 px-2 py-0.5 rounded'>
                                  #{t.task_id}
                                </span>
                                <StatusBadge status={t.status} />
                                <span className='font-medium'>{t.cmd_type}</span>
                              </div>
                              <div className='flex items-center gap-4 text-xs text-tokens-neutral-text-tertiary'>
                                <span>Created: {formatDate(t.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Conversations View */}
              {emailViewMode === 'conversations' && (
                <>
                  {emailResult.conversations.length === 0 ? (
                    <div className='p-8 text-center text-tokens-neutral-text-tertiary bg-tokens-neutral-bg-grey-1 rounded-lg'>
                      No conversations found for this user
                    </div>
                  ) : (
                    <div className='space-y-2'>
                      {emailResult.conversations.map(conv => (
                        <div
                          key={conv.id}
                          onClick={() =>
                            onViewConversation(conv.id, conv.title || `Conversation #${conv.id}`)
                          }
                          className='p-4 bg-white border border-tokens-neutral-border-2 rounded-lg hover:border-tokens-primary-3 cursor-pointer transition-all'
                        >
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-3'>
                              <span className='font-mono text-sm text-tokens-primary-2 bg-tokens-primary-3/30 px-2 py-0.5 rounded'>
                                #{conv.id}
                              </span>
                              <span className='px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded'>
                                {conv.message_count} msg{conv.message_count !== 1 ? 's' : ''}
                              </span>
                              <span className='font-medium truncate max-w-md'>
                                {conv.title || 'Untitled'}
                              </span>
                            </div>
                            <div className='flex items-center gap-4 text-xs text-tokens-neutral-text-tertiary'>
                              <span>Created: {formatDate(conv.created_at)}</span>
                            </div>
                          </div>
                          <div className='mt-1 text-xs text-tokens-neutral-text-tertiary font-mono'>
                            UUID: {conv.uuid}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {workspaceExplorer && (
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold text-tokens-neutral-text-primary'>Workspace</h3>
              <UsageStatRow title='Workspace usage (all-time)' usage={workspaceExplorer.usage} />
              <div className='flex flex-wrap items-center gap-2 text-sm'>
                <span className='font-mono text-tokens-primary-2 bg-tokens-primary-3/30 px-2 py-0.5 rounded'>
                  #{workspaceExplorer.lookup.org_id}
                </span>
                <span className='font-medium'>{workspaceExplorer.lookup.name}</span>
                {workspaceExplorer.lookup.deleted && (
                  <span className='text-xs text-red-600 font-medium'>Deleted</span>
                )}
              </div>
              <p className='text-xs font-mono text-tokens-neutral-text-tertiary break-all'>
                {workspaceExplorer.lookup.uuid}
              </p>
              {workspaceExplorer.lookup.billing_id != null && (
                <p className='text-sm text-tokens-neutral-text-secondary'>
                  Billing #{workspaceExplorer.lookup.billing_id}
                  {workspaceExplorer.lookup.billing_name
                    ? ` · ${workspaceExplorer.lookup.billing_name}`
                    : ''}
                </p>
              )}
              <AdminWorkspaceDrilldown
                orgId={workspaceExplorer.lookup.org_id}
                workspaceUuid={workspaceExplorer.lookup.uuid}
                onTaskSelect={onTaskSelect}
                onViewConversation={onViewConversation}
              />
            </div>
          )}

          {billingExplorer && (
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold text-tokens-neutral-text-primary'>
                Billing account
              </h3>
              <UsageStatRow
                title='Account total (all workspaces)'
                usage={billingExplorer.billingUsage}
              />
              <div className='text-sm text-tokens-neutral-text-secondary'>
                <span className='font-semibold text-tokens-neutral-text-primary'>
                  {billingExplorer.lookup.name ?? '—'}
                </span>
                <span className='font-mono ml-2'>#{billingExplorer.lookup.billing_id}</span>
                {billingExplorer.lookup.billing_email && (
                  <span className='block mt-1'>{billingExplorer.lookup.billing_email}</span>
                )}
              </div>
              <p className='text-xs font-medium text-tokens-neutral-text-secondary uppercase'>
                Workspaces — click to drill into conversations & tasks
              </p>
              <div className='border border-tokens-neutral-border-2 rounded-lg divide-y divide-tokens-neutral-border-2 bg-white'>
                {billingExplorer.lookup.workspaces.map(ws => {
                  const rowUsage = billingExplorer.byOrg[ws.org_id]
                  return (
                    <button
                      key={ws.org_id}
                      type='button'
                      onClick={() => {
                        setBillingDrillOrgId(ws.org_id)
                        setBillingDrillUuid(ws.uuid)
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-tokens-neutral-bg-hover flex flex-wrap items-center justify-between gap-2 ${
                        billingDrillOrgId === ws.org_id ? 'bg-tokens-primary-3' : ''
                      }`}
                    >
                      <div>
                        <div className='font-medium'>{ws.name}</div>
                        <div className='text-xs font-mono text-tokens-neutral-text-tertiary'>
                          #{ws.org_id} · {ws.uuid}
                        </div>
                      </div>
                      {rowUsage && (
                        <div className='text-xs text-tokens-neutral-text-secondary tabular-nums'>
                          {rowUsage.credits.toLocaleString()} cr ·{' '}
                          {rowUsage.billable_flows.toLocaleString()} flows
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
              {billingDrillOrgId != null && (
                <div className='space-y-3'>
                  {billingExplorer.byOrg[billingDrillOrgId] && (
                    <UsageStatRow
                      title='Selected workspace'
                      usage={billingExplorer.byOrg[billingDrillOrgId]}
                    />
                  )}
                  <AdminWorkspaceDrilldown
                    orgId={billingDrillOrgId}
                    workspaceUuid={billingDrillUuid}
                    onTaskSelect={onTaskSelect}
                    onViewConversation={onViewConversation}
                  />
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!emailResult && !workspaceExplorer && !billingExplorer && (
            <div className='flex flex-col items-center justify-center h-64 text-tokens-neutral-text-tertiary'>
              <svg
                className='w-16 h-16 mb-4 opacity-50'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                />
              </svg>
              <p className='max-w-4xl px-4 text-center text-sm sm:text-base'>
                Fields follow priority left to right: billing and workspace (usage and drill-down),
                then task and conversation (modals), then email. Task and conversation open modals;
                email, workspace, and billing include usage where available.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** Browse: secondary outline for domain / workspace / account lists (compact). */
const browseDetailBtnClass =
  'inline-flex min-h-7 shrink-0 items-center justify-center self-center rounded-md border border-tokens-neutral-border-2 bg-white px-1.5 py-1 text-center text-[10px] font-semibold leading-tight text-tokens-primary-2 shadow-sm hover:bg-tokens-neutral-bg-hover sm:px-2 sm:text-[11px]'

/** Browse: secondary outline for task rows — same fixed width as primary for column alignment */
const browseRowSecondaryBtnClass =
  'inline-flex h-7 min-w-[5.25rem] shrink-0 items-center justify-center rounded-md border border-tokens-neutral-border-2 bg-white px-2 text-[11px] font-semibold leading-tight text-tokens-primary-2 shadow-sm hover:bg-tokens-neutral-bg-hover'

/** Labels for browse “info” buttons — avoid generic “Details” */
const BROWSE_BTN_DOMAIN_INFO = 'Info'
const BROWSE_BTN_USER_USAGE = 'Usage'
const BROWSE_BTN_BILLING_USAGE = 'Usage'
const BROWSE_BTN_WORKSPACE_USAGE = 'Usage'
const BROWSE_BTN_CONV_USAGE = 'Usage'
const BROWSE_BTN_TASK_USAGE = 'Usage'
/** Opens SuperAdmin task inspector (summary / detail modal), not usage-only modal */
const BROWSE_BTN_VIEW_TASK = 'View'
const BROWSE_BTN_MSGS = 'Msgs'

/** Spreads non–Mega-QA task list fetches so opening a busy workspace does not N‑parallel the API. */
const BROWSE_TASK_LIST_STAGGER_MS = 80

/** Use the same string for `title` and `aria-label` so tooltips and screen readers stay aligned. */
const BROWSE_HINT_DOMAIN = 'Domain name and user count (no domain-wide usage total)'
const BROWSE_HINT_USER = 'User usage and identifiers'
const BROWSE_HINT_BILLING = 'Billing account usage and identifiers'
const BROWSE_HINT_WORKSPACE = 'Workspace usage and identifiers'
const BROWSE_HINT_VIEW_TASK = 'Open task summary and inspector'
const BROWSE_HINT_TASK_USAGE = 'Task usage and identifiers (this task only)'
const BROWSE_HINT_MSGS = 'Open chat messages for this conversation'
const BROWSE_HINT_CONV_USAGE = 'Conversation usage and identifiers (this conversation only)'

/** Primary row actions — fixed width so View / Msgs align across rows */
const browseMsgsBtnClass =
  'inline-flex h-7 min-w-[5.25rem] shrink-0 items-center justify-center rounded-md bg-tokens-primary-2 px-2 text-[11px] font-semibold leading-none text-white hover:opacity-90'

/** Browse: secondary line under title — slightly stronger than tertiary for scanability */
const browseMetaLineClass = 'text-[11px] leading-snug text-tokens-neutral-text-secondary'

/** Browse: monospace id line — single row, ellipsis (full id in usage modal / APIs). */
const browseRowQueryIdsClass =
  'min-w-0 truncate font-mono text-[10px] leading-snug text-tokens-neutral-text-tertiary'

const browseRowShellClass =
  'flex items-start gap-1.5 border-b border-tokens-neutral-border-2 px-2.5 py-2 hover:bg-tokens-neutral-bg-hover'
/** Mega QA chat rows — subtle tint so Chat vs task scan is easier */
const browseRowShellChatExtraClass = 'bg-tokens-status-link-base-2/[0.06]'
const browseRowChevronSlotClass =
  'flex h-7 w-5 shrink-0 items-center justify-center text-tokens-neutral-icon-default'
/** Fixed width so status / Chat badges share one column edge; titles align */
const browseRowTypeSlotClass =
  'flex w-[7.5rem] shrink-0 flex-col items-stretch justify-start pt-0.5 sm:w-32'
const browseRowActionsSlotClass =
  'flex w-[11.5rem] shrink-0 flex-row items-start justify-end gap-1 pt-0.5'

/** Mega QA threads only — same footprint as status pills in the type column */
function BrowseMegaQaChatBadge() {
  return (
    <span className='flex h-[1.375rem] w-full items-center justify-center rounded-md bg-tokens-status-link-base-2 px-1'>
      <Typography
        fontFamily='geistMono'
        fontSize='labelSm'
        fontWeight='semibold'
        className='truncate text-tokens-primary-2 uppercase tracking-wide'
      >
        Chat
      </Typography>
    </span>
  )
}

const browseColNarrow =
  'flex min-h-0 min-w-0 flex-1 flex-col border-r border-tokens-neutral-border-2'
const browseColWide = 'flex min-h-0 min-w-0 flex-[2.5] flex-col'

// ============================================================================
// Only Mega QA agent mode is a user chat thread; all other modes are task-wrapper sessions.
// ============================================================================
const isMegaQaAgentConversationMode = (mode: string | null): boolean => {
  if (!mode) return false
  return commandTypeFromJSON(mode) === CommandType.MEGA_QA_AGENT_V1
}

const isBrowseListableConversation = (conv: { mode: string | null; task_count: number }): boolean =>
  isMegaQaAgentConversationMode(conv.mode) || conv.task_count > 0

/** Super-admin browse: one task row (no Task badge — task speaks for itself). */
function BrowseSuperAdminTaskRow(props: {
  task: { id?: number; name: string; status: string }
  workspaceUuid: string
  onTaskSelect: (taskId: number, workspaceUuid: string) => void
  onDetailTask?: (taskId: number, name: string) => void
  nestedUnderMegaQaChat: boolean
}) {
  const { task, workspaceUuid, onTaskSelect, onDetailTask, nestedUnderMegaQaChat } = props
  const tid = task.id ?? 0
  return (
    <div
      className={
        nestedUnderMegaQaChat
          ? `${browseRowShellClass} border-l-2 border-l-tokens-primary-2/50 bg-tokens-neutral-bg-grey-1`
          : browseRowShellClass
      }
    >
      <div className={browseRowChevronSlotClass} aria-hidden />
      <div className={browseRowTypeSlotClass}>
        <StatusBadge
          status={task.status}
          className='block w-full truncate rounded-md px-1.5 py-1 text-center text-[10px] leading-tight'
        />
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-0.5 py-0.5'>
        <div
          className='truncate text-sm font-medium leading-snug text-tokens-neutral-text-primary'
          title={task.name}
        >
          {task.name}
        </div>
        <div className={browseRowQueryIdsClass} title={`task_id ${tid}`}>
          task_id · {tid}
        </div>
      </div>
      <div className={browseRowActionsSlotClass}>
        <button
          type='button'
          className={browseMsgsBtnClass}
          title={BROWSE_HINT_VIEW_TASK}
          aria-label={BROWSE_HINT_VIEW_TASK}
          onClick={() => onTaskSelect(tid, workspaceUuid)}
        >
          {BROWSE_BTN_VIEW_TASK}
        </button>
        {onDetailTask ? (
          <button
            type='button'
            className={browseRowSecondaryBtnClass}
            title={BROWSE_HINT_TASK_USAGE}
            aria-label={BROWSE_HINT_TASK_USAGE}
            onClick={() => onDetailTask(tid, task.name)}
          >
            {BROWSE_BTN_TASK_USAGE}
          </button>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Mega QA (MEGA_QA_AGENT_V1) only is rendered as a Chat row. All other DB conversations are
 * task-container shells and are not shown — only their tasks are listed.
 */
const ConversationWithTasks: React.FC<{
  conv: {
    id: number
    /** Conversation UUID for API / DB lookups */
    uuid: string
    title: string
    task_count: number
    message_count: number
    first_task_type: string | null
    mode: string | null
  }
  workspaceUuid: string
  onTaskSelect: (taskId: number, workspaceUuid: string) => void
  onViewConversation: (conversationId: number, title: string) => void
  isExpanded: boolean
  onToggleExpand: () => void
  /** When set (Browse tab), opens usage / identifiers modal for this conversation */
  onDetailConversation?: (conversationId: number, title: string, conversationUuid: string) => void
  /** When set (Browse tab), opens usage / identifiers modal for a task */
  onDetailTask?: (taskId: number, name: string) => void
  /** Index-based delay (ms) before fetching tasks for non–Mega-QA rows; reduces parallel burst. */
  taskListFetchStaggerMs?: number
}> = ({
  conv,
  workspaceUuid,
  onTaskSelect,
  onViewConversation,
  isExpanded,
  onToggleExpand,
  onDetailConversation,
  onDetailTask,
  taskListFetchStaggerMs = 0
}) => {
  const isMegaQa = isMegaQaAgentConversationMode(conv.mode)
  const [staggerReady, setStaggerReady] = useState(() => {
    const ms = !isMegaQa && conv.task_count > 0 ? (taskListFetchStaggerMs ?? 0) : 0
    return ms === 0
  })

  useEffect(() => {
    const ms = !isMegaQa && conv.task_count > 0 ? (taskListFetchStaggerMs ?? 0) : 0
    if (ms === 0) {
      setStaggerReady(true)
      return
    }
    setStaggerReady(false)
    const t = window.setTimeout(() => setStaggerReady(true), ms)
    return () => clearTimeout(t)
  }, [isMegaQa, conv.mode, conv.task_count, conv.id, taskListFetchStaggerMs])

  const conversationIdForTasks = useMemo(() => {
    if (!isMegaQa) {
      if (!staggerReady) return null
      return conv.task_count > 0 ? conv.id : null
    }
    return isExpanded && conv.task_count > 0 ? conv.id : null
  }, [isMegaQa, conv.mode, conv.task_count, isExpanded, conv.id, staggerReady])

  const { tasks, isLoading: tasksLoading } = useTasksList({
    conversationId: conversationIdForTasks
  })

  const displayTitle = useMemo(() => {
    return getConversationDisplayTitle(conv.title, conv.first_task_type || conv.mode)
  }, [conv.title, conv.first_task_type, conv.mode])

  // Non–Mega-QA: never render a “conversation” row; task shells with no tasks are omitted upstream.
  if (!isMegaQa) {
    if (!staggerReady) {
      return (
        <div
          className='border-b border-tokens-neutral-border-2 px-3 py-2 text-xs text-tokens-neutral-text-tertiary'
          aria-busy='true'
          aria-label='Loading conversation tasks'
        >
          Loading…
        </div>
      )
    }
    if (tasksLoading) {
      return (
        <div className='border-b border-tokens-neutral-border-2 px-3 py-2 text-xs text-tokens-neutral-text-secondary'>
          Loading…
        </div>
      )
    }
    if (tasks.length === 0) {
      return (
        <div className='border-b border-tokens-neutral-border-2 px-3 py-2 text-xs text-tokens-neutral-text-tertiary'>
          No tasks
        </div>
      )
    }
    return (
      <>
        {tasks.map((task, idx) => (
          <BrowseSuperAdminTaskRow
            key={task.id ?? idx}
            task={task}
            workspaceUuid={workspaceUuid}
            onTaskSelect={onTaskSelect}
            onDetailTask={onDetailTask}
            nestedUnderMegaQaChat={false}
          />
        ))}
      </>
    )
  }

  // Mega QA, no tasks — chat-only row
  if (conv.task_count === 0) {
    const chatDetailTitle = `${conv.message_count} message${conv.message_count !== 1 ? 's' : ''} · ${conv.uuid}`
    return (
      <div className={`${browseRowShellClass} ${browseRowShellChatExtraClass}`}>
        <div className={browseRowChevronSlotClass} aria-hidden />
        <div className={browseRowTypeSlotClass}>
          <BrowseMegaQaChatBadge />
        </div>
        <div className='flex min-w-0 flex-1 flex-col gap-0.5 py-0.5'>
          <div
            className='truncate text-sm font-medium leading-snug text-tokens-neutral-text-primary'
            title={displayTitle}
          >
            {displayTitle}
          </div>
          <div className={browseRowQueryIdsClass} title={chatDetailTitle}>
            conversation_id · {conv.id}
          </div>
        </div>
        <div className={browseRowActionsSlotClass}>
          <button
            type='button'
            title={BROWSE_HINT_MSGS}
            aria-label={BROWSE_HINT_MSGS}
            onClick={() => onViewConversation(conv.id, conv.title)}
            className={browseMsgsBtnClass}
          >
            {BROWSE_BTN_MSGS}
          </button>
          {onDetailConversation ? (
            <button
              type='button'
              className={browseRowSecondaryBtnClass}
              title={BROWSE_HINT_CONV_USAGE}
              aria-label={BROWSE_HINT_CONV_USAGE}
              onClick={() => onDetailConversation(conv.id, conv.title, conv.uuid)}
            >
              {BROWSE_BTN_CONV_USAGE}
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  // Mega QA with tasks — accordion header + nested tasks
  const accordionMetaTitle = `${conv.task_count} task${conv.task_count !== 1 ? 's' : ''} · ${conv.message_count} message${conv.message_count !== 1 ? 's' : ''} · conversation_id ${conv.id} · ${conv.uuid}`

  return (
    <div>
      <div
        className={`${browseRowShellClass} ${browseRowShellChatExtraClass} cursor-pointer`}
        onClick={onToggleExpand}
      >
        <div className={browseRowChevronSlotClass}>
          <svg
            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
          </svg>
        </div>
        <div className={browseRowTypeSlotClass}>
          <BrowseMegaQaChatBadge />
        </div>
        <div className='flex min-w-0 flex-1 flex-col gap-0.5 py-0.5'>
          <div
            className='truncate text-sm font-medium leading-snug text-tokens-neutral-text-primary'
            title={displayTitle}
          >
            {displayTitle}
          </div>
          <div className={browseRowQueryIdsClass} title={accordionMetaTitle}>
            conversation_id · {conv.id}
          </div>
        </div>
        <div className={browseRowActionsSlotClass} onClick={e => e.stopPropagation()}>
          <button
            type='button'
            title={BROWSE_HINT_MSGS}
            aria-label={BROWSE_HINT_MSGS}
            onClick={() => onViewConversation(conv.id, conv.title)}
            className={browseMsgsBtnClass}
          >
            {BROWSE_BTN_MSGS}
          </button>
          {onDetailConversation ? (
            <button
              type='button'
              className={browseRowSecondaryBtnClass}
              title={BROWSE_HINT_CONV_USAGE}
              aria-label={BROWSE_HINT_CONV_USAGE}
              onClick={() => onDetailConversation(conv.id, conv.title, conv.uuid)}
            >
              {BROWSE_BTN_CONV_USAGE}
            </button>
          ) : null}
        </div>
      </div>
      {isExpanded ? (
        <div className='border-t border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1'>
          {tasksLoading ? (
            <div className='px-3 py-2 pl-8 text-xs text-tokens-neutral-text-secondary'>
              Loading…
            </div>
          ) : tasks.length === 0 ? (
            <div className='px-3 py-2 pl-8 text-xs text-tokens-neutral-text-tertiary'>No tasks</div>
          ) : (
            tasks.map((task, idx) => (
              <BrowseSuperAdminTaskRow
                key={task.id ?? idx}
                task={task}
                workspaceUuid={workspaceUuid}
                onTaskSelect={onTaskSelect}
                onDetailTask={onDetailTask}
                nestedUnderMegaQaChat
              />
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}

/** Credits → Flows → Input / output tokens in equal columns (used in Find + Browse usage panels) */
function UsageStatRow({
  title,
  usage
}: {
  title?: string
  usage: AdminUsageSummary | null | undefined
}) {
  if (!usage) return null
  return (
    <div className='rounded-lg border border-tokens-neutral-border-2 bg-white px-3 py-3 sm:px-4'>
      {title ? (
        <p className='mb-3 text-sm font-medium leading-snug text-tokens-neutral-text-primary'>
          {title}
        </p>
      ) : null}
      <div className='grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4'>
        <div className='min-w-0 text-center'>
          <div className='text-[11px] font-medium text-tokens-neutral-text-secondary'>Credits</div>
          <div className='mt-1 text-base font-semibold tabular-nums text-tokens-primary-2 sm:text-lg'>
            {(usage.credits ?? 0).toLocaleString()}
          </div>
        </div>
        <div className='min-w-0 text-center'>
          <div className='text-[11px] font-medium text-tokens-neutral-text-secondary'>Flows</div>
          <div className='mt-1 text-base font-semibold tabular-nums text-tokens-neutral-text-primary sm:text-lg'>
            {(usage.billable_flows ?? 0).toLocaleString()}
          </div>
        </div>
        <div className='min-w-0 text-center'>
          <div className='text-[11px] font-medium text-tokens-neutral-text-secondary'>
            Input tokens
          </div>
          <div className='mt-1 text-base font-semibold tabular-nums text-tokens-neutral-text-primary sm:text-lg'>
            {(usage.input_tokens ?? 0).toLocaleString()}
          </div>
        </div>
        <div className='min-w-0 text-center'>
          <div className='text-[11px] font-medium text-tokens-neutral-text-secondary'>
            Output tokens
          </div>
          <div className='mt-1 text-base font-semibold tabular-nums text-tokens-neutral-text-primary sm:text-lg'>
            {(usage.output_tokens ?? 0).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  )
}

type BrowseDetailTarget =
  | { kind: 'domain'; domain: string; userCount: number }
  | { kind: 'email'; email: string; userId: number }
  | { kind: 'account'; name: string; billingId: number | null }
  | { kind: 'workspace'; orgId: number; uuid: string; name: string }
  | { kind: 'conversation'; conversationId: number; title: string; conversationUuid: string }
  | { kind: 'task'; taskId: number; name: string }

function browseModalTitle(target: BrowseDetailTarget): string {
  switch (target.kind) {
    case 'domain':
      return 'Domain'
    case 'email':
      return 'User'
    case 'account':
      return 'Billing account'
    case 'workspace':
      return 'Workspace'
    case 'conversation':
      return 'Conversation'
    case 'task':
      return 'Task'
    default:
      return ''
  }
}

function browseModalSubtitle(target: BrowseDetailTarget): string | null {
  switch (target.kind) {
    case 'domain':
      return 'No domain-wide credit total. Use User usage or Workspace usage on a row below.'
    case 'conversation':
      return 'Usage and identifiers for this conversation only (not the whole workspace).'
    case 'task':
      return 'Usage and identifiers for this task only (not the parent conversation).'
    case 'email':
      return 'Identifiers below; usage is aggregated across all workspaces for this user (all time).'
    case 'account':
      return target.billingId == null
        ? null
        : 'Identifiers below; usage is aggregated for this billing account (all time).'
    case 'workspace':
      return 'Identifiers below; usage is for this workspace / org (all time).'
    default:
      return null
  }
}

function BrowseDetailsModalBody({ target }: { target: BrowseDetailTarget }) {
  const [usage, setUsage] = useState<AdminUsageSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setUsage(null)
    setError(null)
    if (target.kind === 'domain') {
      setLoading(false)
      return
    }
    if (target.kind === 'account' && target.billingId == null) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        let u: AdminUsageSummary
        if (target.kind === 'email') {
          u = await getAdminUsageSummary({ scope: 'user', userId: target.userId })
        } else if (target.kind === 'account' && target.billingId != null) {
          u = await getAdminUsageSummary({ scope: 'billing', billingId: target.billingId })
        } else if (target.kind === 'workspace') {
          u = await getAdminUsageSummary({ scope: 'workspace', orgId: target.orgId })
        } else if (target.kind === 'conversation') {
          u = await getAdminUsageSummary({
            scope: 'conversation',
            conversationId: target.conversationId
          })
        } else if (target.kind === 'task') {
          u = await getAdminUsageSummary({ scope: 'task', taskId: target.taskId })
        } else {
          return
        }
        if (!cancelled) setUsage(u)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load usage')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [target])

  const subtitle = browseModalSubtitle(target)

  const browseIdSectionClass =
    'rounded-lg border border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1 px-3 py-3 sm:px-4'
  const browseSectionLabelClass = 'mb-2 text-[11px] font-medium text-tokens-neutral-text-secondary'

  return (
    <div className='p-5 sm:p-6'>
      <header className='mb-4 border-b border-tokens-neutral-border-2 pb-3'>
        <h2 className='text-base font-semibold leading-tight text-tokens-neutral-text-primary'>
          {browseModalTitle(target)}
        </h2>
        {subtitle && (
          <p className='mt-1.5 text-xs leading-relaxed text-tokens-neutral-text-secondary'>
            {subtitle}
          </p>
        )}
      </header>

      <div className='space-y-3'>
        {target.kind === 'domain' && (
          <div className={browseIdSectionClass}>
            <div className={browseSectionLabelClass}>Domain</div>
            <div className='text-sm text-tokens-neutral-text-primary'>{target.domain}</div>
            <p className='mt-2 text-xs text-tokens-neutral-text-tertiary'>
              {target.userCount} user{target.userCount !== 1 ? 's' : ''} in this domain
            </p>
          </div>
        )}

        {target.kind === 'email' && (
          <div className={browseIdSectionClass}>
            <div className={browseSectionLabelClass}>Identifiers</div>
            <div className='break-all text-sm text-tokens-neutral-text-primary'>{target.email}</div>
            <div className='mt-2 font-mono text-xs text-tokens-neutral-text-tertiary'>
              user_id: {target.userId}
            </div>
          </div>
        )}

        {target.kind === 'account' && (
          <div className={browseIdSectionClass}>
            <div className={browseSectionLabelClass}>Identifiers</div>
            <div className='text-sm text-tokens-neutral-text-primary'>{target.name}</div>
            {target.billingId != null ? (
              <div className='mt-2 font-mono text-xs text-tokens-neutral-text-tertiary'>
                billing_id: {target.billingId}
              </div>
            ) : (
              <p className='mt-2 text-xs text-tokens-neutral-text-tertiary'>
                No billing account linked; usage at this scope is unavailable.
              </p>
            )}
          </div>
        )}

        {target.kind === 'workspace' && (
          <div className={browseIdSectionClass}>
            <div className={browseSectionLabelClass}>Identifiers</div>
            <div className='text-sm text-tokens-neutral-text-primary'>{target.name}</div>
            <div className='mt-2 break-all font-mono text-xs text-tokens-neutral-text-tertiary'>
              org_id: {target.orgId}
            </div>
            <div className='mt-1 break-all font-mono text-xs text-tokens-neutral-text-tertiary'>
              uuid: {target.uuid}
            </div>
          </div>
        )}

        {target.kind === 'conversation' && (
          <div className={browseIdSectionClass}>
            <div className={browseSectionLabelClass}>Identifiers</div>
            <div className='truncate text-sm text-tokens-neutral-text-primary' title={target.title}>
              {target.title || 'Untitled'}
            </div>
            <div className='mt-2 font-mono text-xs text-tokens-neutral-text-tertiary'>
              conversation_id: {target.conversationId}
            </div>
            <div className='mt-1 break-all font-mono text-xs text-tokens-neutral-text-tertiary'>
              uuid: {target.conversationUuid}
            </div>
          </div>
        )}

        {target.kind === 'task' && (
          <div className={browseIdSectionClass}>
            <div className={browseSectionLabelClass}>Identifiers</div>
            <div className='truncate text-sm text-tokens-neutral-text-primary' title={target.name}>
              {target.name || 'Untitled'}
            </div>
            <div className='mt-2 font-mono text-xs text-tokens-neutral-text-tertiary'>
              task_id: {target.taskId}
            </div>
          </div>
        )}

        {target.kind === 'account' && target.billingId == null ? null : target.kind ===
          'domain' ? null : (
          <>
            {loading && (
              <div className='flex items-center gap-2 py-2 text-sm text-tokens-neutral-text-secondary'>
                <Spinner size='sm' /> Loading usage…
              </div>
            )}
            {error && <p className='text-sm text-red-600'>{error}</p>}
            {!loading && !error && (
              <div>
                <div className={browseSectionLabelClass}>Usage (all time)</div>
                <UsageStatRow usage={usage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function BrowseDetailsModal({
  target,
  onClose
}: {
  target: BrowseDetailTarget | null
  onClose: () => void
}) {
  return (
    <Modal
      isOpen={target != null}
      onClose={onClose}
      className='max-w-md w-full'
      hideCloseButton={false}
    >
      {target ? <BrowseDetailsModalBody target={target} /> : null}
    </Modal>
  )
}

const AdminWorkspaceDrilldown: React.FC<{
  orgId: number
  workspaceUuid: string
  onTaskSelect: (taskId: number, workspaceUuid: string) => void
  onViewConversation: (conversationId: number, title: string) => void
}> = ({ orgId, workspaceUuid, onTaskSelect, onViewConversation }) => {
  const { conversations, isLoading } = useConversationsList({ orgId })
  const [expandedConversations, setExpandedConversations] = useState<Set<number>>(new Set())

  const toggleConversation = useCallback((convId: number) => {
    setExpandedConversations(prev => {
      const next = new Set(prev)
      if (next.has(convId)) next.delete(convId)
      else next.add(convId)
      return next
    })
  }, [])

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 py-4 text-tokens-neutral-text-secondary'>
        <Spinner size='sm' /> Loading conversations…
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <p className='text-sm text-tokens-neutral-text-tertiary py-2'>
        No conversations in this workspace.
      </p>
    )
  }

  const listable = conversations.filter(isBrowseListableConversation)
  if (listable.length === 0) {
    return (
      <p className='text-sm text-tokens-neutral-text-tertiary py-2'>
        No Mega QA chats or agent tasks to show in this workspace.
      </p>
    )
  }

  return (
    <div className='border border-tokens-neutral-border-2 rounded-lg overflow-hidden bg-white max-h-[min(420px,50vh)] overflow-y-auto'>
      <div className='px-3 py-2 bg-tokens-neutral-bg-grey-2 text-xs font-semibold text-tokens-neutral-text-secondary uppercase'>
        Chats & agent tasks ({listable.length})
      </div>
      {listable.map((conv, idx) => (
        <ConversationWithTasks
          key={conv.id}
          conv={conv}
          workspaceUuid={workspaceUuid}
          onTaskSelect={onTaskSelect}
          onViewConversation={onViewConversation}
          isExpanded={expandedConversations.has(conv.id)}
          onToggleExpand={() => toggleConversation(conv.id)}
          taskListFetchStaggerMs={idx * BROWSE_TASK_LIST_STAGGER_MS}
        />
      ))}
    </div>
  )
}

// ============================================================================
// Browse Tab - All data with search filter
// ============================================================================
const BrowseTab: React.FC<{
  onTaskSelect: (taskId: number, workspaceUuid: string) => void
  onViewConversation: (conversationId: number, title: string) => void
  searchParams: URLSearchParams
  setSearchParams: ReturnType<typeof useSearchParams>[1]
}> = ({ onTaskSelect, onViewConversation, searchParams, setSearchParams }) => {
  // Selection state - sync with URL
  const [searchFilter, setSearchFilter] = useState(() => searchParams.get('search') || '')
  const [browseData, setBrowseData] = useState<BrowseDomain[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Selection state - sync with URL
  const [selectedDomain, setSelectedDomain] = useState<string | null>(
    () => searchParams.get('domain') || null
  )
  const [selectedEmail, setSelectedEmail] = useState<string | null>(
    () => searchParams.get('browseEmail') || null
  )
  const [selectedAccount, setSelectedAccount] = useState<{
    id: number | null
    name: string
  } | null>(() => {
    const accId = searchParams.get('accountId')
    const accName = searchParams.get('accountName')
    if (accName) {
      return { id: accId ? parseInt(accId) : null, name: accName }
    }
    return null
  })
  const [selectedWorkspace, setSelectedWorkspace] = useState<{
    id: number
    uuid: string
    name: string
  } | null>(() => {
    const wsId = searchParams.get('workspaceId')
    const wsUuid = searchParams.get('workspaceUuid')
    const wsName = searchParams.get('workspaceName')
    if (wsId && wsUuid && wsName) {
      return { id: parseInt(wsId), uuid: wsUuid, name: wsName }
    }
    return null
  })

  const [browseDetailTarget, setBrowseDetailTarget] = useState<BrowseDetailTarget | null>(null)

  // Sync browse state to URL query params
  useEffect(() => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev)
        if (searchFilter) params.set('search', searchFilter)
        else params.delete('search')
        if (selectedDomain) params.set('domain', selectedDomain)
        else params.delete('domain')
        if (selectedEmail) params.set('browseEmail', selectedEmail)
        else params.delete('browseEmail')
        if (selectedAccount) {
          if (selectedAccount.id) params.set('accountId', selectedAccount.id.toString())
          else params.delete('accountId')
          params.set('accountName', selectedAccount.name)
        } else {
          params.delete('accountId')
          params.delete('accountName')
        }
        if (selectedWorkspace) {
          params.set('workspaceId', selectedWorkspace.id.toString())
          params.set('workspaceUuid', selectedWorkspace.uuid)
          params.set('workspaceName', selectedWorkspace.name)
        } else {
          params.delete('workspaceId')
          params.delete('workspaceUuid')
          params.delete('workspaceName')
        }
        return params
      },
      { replace: true }
    )
  }, [
    searchFilter,
    selectedDomain,
    selectedEmail,
    selectedAccount,
    selectedWorkspace,
    setSearchParams
  ])

  // Conversations for selected workspace
  const { conversations, isLoading: isLoadingConversations } = useConversationsList({
    orgId: selectedWorkspace?.id ?? null
  })
  const listableBrowseConversations = useMemo(
    () => conversations.filter(isBrowseListableConversation),
    [conversations]
  )

  // Track expanded conversations
  const [expandedConversations, setExpandedConversations] = useState<Set<number>>(new Set())

  // Track if initial load from URL
  const isInitialLoadRef = useRef(true)
  const prevSearchFilterRef = useRef(searchFilter)
  const prevSelectedDomainRef = useRef(selectedDomain)
  const prevSelectedEmailRef = useRef(selectedEmail)
  const prevSelectedAccountRef = useRef(selectedAccount)

  // Load all browse data on mount
  useEffect(() => {
    setIsLoading(true)
    setError(null)
    getBrowseData()
      .then(response => {
        setBrowseData(response.data)
        // After initial data load, mark as not initial load
        setTimeout(() => {
          isInitialLoadRef.current = false
        }, 100)
      })
      .catch(err => {
        setError(err.message || 'Failed to load browse data')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  // Filter data based on search keyword
  const filteredData = useMemo(() => {
    if (!searchFilter.trim()) return browseData

    const keyword = searchFilter.toLowerCase()
    const result: BrowseDomain[] = []

    for (const domain of browseData) {
      const domainMatches = domain.domain.toLowerCase().includes(keyword)

      const filteredEmails: BrowseEmail[] = []
      for (const emailData of domain.emails) {
        const emailMatches = emailData.email.toLowerCase().includes(keyword)
        const nameMatches = emailData.name?.toLowerCase().includes(keyword)

        // Filter accounts and their workspaces
        const filteredAccounts: BrowseAccount[] = []
        for (const account of emailData.accounts) {
          const accountMatches = account.name.toLowerCase().includes(keyword)
          const filteredWorkspaces = account.workspaces.filter(
            ws => ws.name.toLowerCase().includes(keyword) || ws.uuid.toLowerCase().includes(keyword)
          )

          if (
            domainMatches ||
            emailMatches ||
            nameMatches ||
            accountMatches ||
            filteredWorkspaces.length > 0
          ) {
            filteredAccounts.push({
              ...account,
              workspaces: filteredWorkspaces.length > 0 ? filteredWorkspaces : account.workspaces
            })
          }
        }

        if (filteredAccounts.length > 0) {
          filteredEmails.push({
            ...emailData,
            accounts: filteredAccounts
          })
        }
      }

      if (filteredEmails.length > 0) {
        result.push({ domain: domain.domain, emails: filteredEmails })
      }
    }

    return result
  }, [browseData, searchFilter])

  // Get current emails for selected domain
  const currentEmails = useMemo(() => {
    if (!selectedDomain) return []
    const domainData = filteredData.find(d => d.domain === selectedDomain)
    return domainData?.emails || []
  }, [filteredData, selectedDomain])

  // Get current accounts for selected email
  const currentAccounts = useMemo(() => {
    if (!selectedEmail) return []
    const emailData = currentEmails.find(e => e.email === selectedEmail)
    return emailData?.accounts || []
  }, [currentEmails, selectedEmail])

  // Get current workspaces for selected account
  const currentWorkspaces = useMemo(() => {
    if (!selectedAccount) return []
    const accountData = currentAccounts.find(
      a => a.id === selectedAccount.id && a.name === selectedAccount.name
    )
    return accountData?.workspaces || []
  }, [currentAccounts, selectedAccount])

  // Reset selections when filter changes (only if not initial load)
  useEffect(() => {
    if (isInitialLoadRef.current || searchFilter === prevSearchFilterRef.current) {
      prevSearchFilterRef.current = searchFilter
      return
    }
    prevSearchFilterRef.current = searchFilter
    setSelectedDomain(null)
    setSelectedEmail(null)
    setSelectedAccount(null)
    setSelectedWorkspace(null)
    setExpandedConversations(new Set())
  }, [searchFilter])

  // Reset downstream selections (only if not initial load)
  useEffect(() => {
    if (isInitialLoadRef.current || selectedDomain === prevSelectedDomainRef.current) {
      prevSelectedDomainRef.current = selectedDomain
      return
    }
    prevSelectedDomainRef.current = selectedDomain
    setSelectedEmail(null)
    setSelectedAccount(null)
    setSelectedWorkspace(null)
    setExpandedConversations(new Set())
  }, [selectedDomain])

  useEffect(() => {
    if (isInitialLoadRef.current || selectedEmail === prevSelectedEmailRef.current) {
      prevSelectedEmailRef.current = selectedEmail
      return
    }
    prevSelectedEmailRef.current = selectedEmail
    setSelectedAccount(null)
    setSelectedWorkspace(null)
    setExpandedConversations(new Set())
  }, [selectedEmail])

  useEffect(() => {
    if (isInitialLoadRef.current || selectedAccount === prevSelectedAccountRef.current) {
      prevSelectedAccountRef.current = selectedAccount
      return
    }
    prevSelectedAccountRef.current = selectedAccount
    setSelectedWorkspace(null)
    setExpandedConversations(new Set())
  }, [selectedAccount])

  useEffect(() => {
    if (isInitialLoadRef.current) return
    setExpandedConversations(new Set())
  }, [selectedWorkspace])

  const toggleConversation = useCallback((convId: number) => {
    setExpandedConversations(prev => {
      const next = new Set(prev)
      if (next.has(convId)) {
        next.delete(convId)
      } else {
        next.add(convId)
      }
      return next
    })
  }, [])

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center'>
          <Spinner size='lg' />
          <p className='mt-4 text-tokens-neutral-text-secondary select-none'>
            Loading browse data...
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center text-red-600'>
          <p>{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Search Filter */}
      <div className='shrink-0 px-4 py-3 border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1'>
        <Input
          placeholder='Search by domain, email, account, or workspace...'
          value={searchFilter}
          onChange={e => setSearchFilter(e.target.value)}
          classNames={{ base: 'w-full', inputWrapper: 'h-9 bg-white' }}
          startContent={
            <svg
              className='w-4 h-4 text-tokens-neutral-icon-default'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
              />
            </svg>
          }
        />
      </div>

      {/* Columns — last column flexes wider so conversations are not cramped */}
      <div className='flex min-h-0 min-w-0 flex-1 overflow-hidden'>
        {/* Domains */}
        <div className={browseColNarrow}>
          <div className='shrink-0 border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-2 px-2.5 py-2'>
            <h3 className='text-xs font-semibold uppercase text-tokens-neutral-text-secondary'>
              Domains ({filteredData.length})
            </h3>
          </div>
          <ul className='flex-1 overflow-y-auto'>
            {filteredData.map((domainData, idx) => (
              <li
                key={idx}
                className={`flex items-center gap-2 border-b border-tokens-neutral-border-2 px-2.5 py-2.5 text-sm ${
                  selectedDomain === domainData.domain
                    ? 'bg-tokens-primary-3 font-medium text-tokens-primary-2 ring-1 ring-inset ring-tokens-primary-2/35'
                    : 'cursor-pointer hover:bg-tokens-neutral-bg-hover'
                }`}
              >
                <div
                  className='min-w-0 flex-1 cursor-pointer'
                  onClick={() => setSelectedDomain(domainData.domain)}
                >
                  <div className='truncate text-sm font-medium leading-tight'>
                    {domainData.domain}
                  </div>
                  <div className={`${browseMetaLineClass} mt-0.5`}>
                    {domainData.emails.length} user{domainData.emails.length !== 1 ? 's' : ''}
                  </div>
                </div>
                <button
                  type='button'
                  className={browseDetailBtnClass}
                  title={BROWSE_HINT_DOMAIN}
                  aria-label={BROWSE_HINT_DOMAIN}
                  onClick={e => {
                    e.stopPropagation()
                    setBrowseDetailTarget({
                      kind: 'domain',
                      domain: domainData.domain,
                      userCount: domainData.emails.length
                    })
                  }}
                >
                  {BROWSE_BTN_DOMAIN_INFO}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Emails */}
        <div className={browseColNarrow}>
          <div className='shrink-0 border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-2 px-2.5 py-2'>
            <h3 className='text-xs font-semibold uppercase text-tokens-neutral-text-secondary'>
              Emails {currentEmails.length > 0 && `(${currentEmails.length})`}
            </h3>
          </div>
          <ul className='flex-1 overflow-y-auto'>
            {!selectedDomain ? (
              <li className='p-3 text-center text-tokens-neutral-text-tertiary text-sm'>
                Select domain
              </li>
            ) : currentEmails.length === 0 ? (
              <li className='p-3 text-center text-tokens-neutral-text-tertiary text-sm'>
                No emails
              </li>
            ) : (
              currentEmails.map((emailData, idx) => (
                <li
                  key={idx}
                  className={`flex items-center gap-2 border-b border-tokens-neutral-border-2 px-2.5 py-2.5 text-sm ${
                    selectedEmail === emailData.email
                      ? 'bg-tokens-primary-3 font-medium text-tokens-primary-2 ring-1 ring-inset ring-tokens-primary-2/35'
                      : 'cursor-pointer hover:bg-tokens-neutral-bg-hover'
                  }`}
                >
                  <div
                    className='min-w-0 flex-1 cursor-pointer'
                    onClick={() => setSelectedEmail(emailData.email)}
                  >
                    <div className='truncate text-sm font-medium leading-tight'>
                      {emailData.email}
                    </div>
                    <div className={`${browseMetaLineClass} mt-0.5`}>
                      {emailData.accounts.length} account
                      {emailData.accounts.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <button
                    type='button'
                    className={browseDetailBtnClass}
                    title={BROWSE_HINT_USER}
                    aria-label={BROWSE_HINT_USER}
                    onClick={e => {
                      e.stopPropagation()
                      setBrowseDetailTarget({
                        kind: 'email',
                        email: emailData.email,
                        userId: emailData.user_id
                      })
                    }}
                  >
                    {BROWSE_BTN_USER_USAGE}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* Accounts */}
        <div className={browseColNarrow}>
          <div className='shrink-0 border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-2 px-2.5 py-2'>
            <h3 className='text-xs font-semibold uppercase text-tokens-neutral-text-secondary'>
              Accounts {currentAccounts.length > 0 && `(${currentAccounts.length})`}
            </h3>
          </div>
          <ul className='flex-1 overflow-y-auto'>
            {!selectedEmail ? (
              <li className='p-3 text-center text-tokens-neutral-text-tertiary text-sm'>
                Select email
              </li>
            ) : currentAccounts.length === 0 ? (
              <li className='p-3 text-center text-tokens-neutral-text-tertiary text-sm'>
                No accounts
              </li>
            ) : (
              currentAccounts.map((account, idx) => (
                <li
                  key={idx}
                  className={`flex items-center gap-2 border-b border-tokens-neutral-border-2 px-2.5 py-2.5 text-sm ${
                    selectedAccount?.id === account.id && selectedAccount?.name === account.name
                      ? 'bg-tokens-primary-3 font-medium text-tokens-primary-2 ring-1 ring-inset ring-tokens-primary-2/35'
                      : 'cursor-pointer hover:bg-tokens-neutral-bg-hover'
                  }`}
                >
                  <div
                    className='min-w-0 flex-1 cursor-pointer'
                    onClick={() => setSelectedAccount({ id: account.id, name: account.name })}
                  >
                    <div className='truncate text-sm font-medium leading-tight'>{account.name}</div>
                    <div className={`${browseMetaLineClass} mt-0.5`}>
                      {account.workspaces.length} workspace
                      {account.workspaces.length !== 1 ? 's' : ''}
                      {account.id != null ? ` · #${account.id}` : ''}
                    </div>
                  </div>
                  <button
                    type='button'
                    className={browseDetailBtnClass}
                    title={BROWSE_HINT_BILLING}
                    aria-label={BROWSE_HINT_BILLING}
                    onClick={e => {
                      e.stopPropagation()
                      setBrowseDetailTarget({
                        kind: 'account',
                        name: account.name,
                        billingId: account.id
                      })
                    }}
                  >
                    {BROWSE_BTN_BILLING_USAGE}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* Workspaces */}
        <div className={browseColNarrow}>
          <div className='shrink-0 border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-2 px-2.5 py-2'>
            <h3 className='text-xs font-semibold uppercase text-tokens-neutral-text-secondary'>
              Workspaces {currentWorkspaces.length > 0 && `(${currentWorkspaces.length})`}
            </h3>
          </div>
          <ul className='flex-1 overflow-y-auto'>
            {!selectedAccount ? (
              <li className='p-3 text-center text-tokens-neutral-text-tertiary text-sm'>
                Select account
              </li>
            ) : currentWorkspaces.length === 0 ? (
              <li className='p-3 text-center text-tokens-neutral-text-tertiary text-sm'>
                No workspaces
              </li>
            ) : (
              currentWorkspaces.map((ws, idx) => (
                <li
                  key={idx}
                  className={`flex items-center gap-2 border-b border-tokens-neutral-border-2 px-2.5 py-2.5 text-sm ${
                    selectedWorkspace?.id === ws.id
                      ? 'bg-tokens-primary-3 font-medium text-tokens-primary-2 ring-1 ring-inset ring-tokens-primary-2/35'
                      : 'cursor-pointer hover:bg-tokens-neutral-bg-hover'
                  }`}
                >
                  <div
                    className='min-w-0 flex-1 cursor-pointer'
                    onClick={() =>
                      setSelectedWorkspace({ id: ws.id, uuid: ws.uuid, name: ws.name })
                    }
                  >
                    <div className='truncate text-sm font-medium leading-tight'>{ws.name}</div>
                    <div
                      className={`${browseMetaLineClass} mt-0.5`}
                      title={`org_id ${ws.id} · ${ws.uuid}`}
                    >
                      {ws.role} · org #{ws.id}
                    </div>
                  </div>
                  <button
                    type='button'
                    className={browseDetailBtnClass}
                    title={BROWSE_HINT_WORKSPACE}
                    aria-label={BROWSE_HINT_WORKSPACE}
                    onClick={e => {
                      e.stopPropagation()
                      setBrowseDetailTarget({
                        kind: 'workspace',
                        orgId: ws.id,
                        uuid: ws.uuid,
                        name: ws.name
                      })
                    }}
                  >
                    {BROWSE_BTN_WORKSPACE_USAGE}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* Conversations & Tasks (hierarchical) */}
        <div className={browseColWide}>
          <div className='shrink-0 border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-2 px-2.5 py-2'>
            <h3 className='text-xs font-semibold uppercase text-tokens-neutral-text-secondary'>
              Chats & agent tasks{' '}
              {listableBrowseConversations.length > 0 && `(${listableBrowseConversations.length})`}
            </h3>
          </div>
          <div className='flex-1 overflow-y-auto'>
            {!selectedWorkspace ? (
              <div className='p-3 text-center text-tokens-neutral-text-tertiary text-sm'>
                Select workspace
              </div>
            ) : isLoadingConversations ? (
              <div className='p-3 flex items-center justify-center'>
                <Spinner size='sm' />
              </div>
            ) : conversations.length === 0 ? (
              <div className='p-3 text-center text-tokens-neutral-text-tertiary text-sm'>
                No conversations
              </div>
            ) : listableBrowseConversations.length === 0 ? (
              <div className='p-3 text-center text-tokens-neutral-text-tertiary text-sm'>
                No Mega QA chats or agent tasks to show
              </div>
            ) : (
              listableBrowseConversations.map((conv, idx) => (
                <ConversationWithTasks
                  key={conv.id}
                  conv={conv}
                  workspaceUuid={selectedWorkspace.uuid}
                  onTaskSelect={onTaskSelect}
                  onViewConversation={onViewConversation}
                  isExpanded={expandedConversations.has(conv.id)}
                  onToggleExpand={() => toggleConversation(conv.id)}
                  taskListFetchStaggerMs={idx * BROWSE_TASK_LIST_STAGGER_MS}
                  onDetailConversation={(conversationId, title, conversationUuid) =>
                    setBrowseDetailTarget({
                      kind: 'conversation',
                      conversationId,
                      title,
                      conversationUuid
                    })
                  }
                  onDetailTask={(taskId, name) =>
                    setBrowseDetailTarget({ kind: 'task', taskId, name })
                  }
                />
              ))
            )}
          </div>
        </div>
      </div>

      <BrowseDetailsModal target={browseDetailTarget} onClose={() => setBrowseDetailTarget(null)} />
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================
const TasksTabContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Active tab - sync with URL
  const [activeTab, setActiveTab] = useState<'find' | 'browse'>(() => {
    const subtab = searchParams.get('subtab')
    return subtab === 'browse' ? 'browse' : 'find'
  })

  // Sync activeTab to URL
  useEffect(() => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev)
        if (activeTab !== 'find') params.set('subtab', activeTab)
        else params.delete('subtab')
        return params
      },
      { replace: true }
    )
  }, [activeTab, setSearchParams])

  // Task modal
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null)
  const [selectedWorkspaceUuid, setSelectedWorkspaceUuid] = useState<string | null>(null)
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)

  // Conversation modal
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false)
  const [viewingConversationTitle, setViewingConversationTitle] = useState('')
  const [viewingConversationId, setViewingConversationId] = useState<number | null>(null)

  const handleTaskSelect = (taskId: number, workspaceUuid: string) => {
    setSelectedTaskId(taskId)
    setSelectedWorkspaceUuid(workspaceUuid)
    setIsTaskModalOpen(true)
  }

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false)
    setSelectedTaskId(null)
    setSelectedWorkspaceUuid(null)
  }

  const handleViewConversation = useCallback((conversationId: number, title: string) => {
    setViewingConversationTitle(title)
    setViewingConversationId(conversationId)
    setIsConversationModalOpen(true)
  }, [])

  const handleCloseConversationModal = () => {
    setIsConversationModalOpen(false)
    setViewingConversationTitle('')
    setViewingConversationId(null)
  }

  return (
    <div className='flex flex-col h-full bg-white'>
      {/* Tabs */}
      <div className='shrink-0 border-b border-tokens-neutral-border-2 px-6'>
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={key => setActiveTab(key as 'find' | 'browse')}
          variant='underlined'
          classNames={{
            tabList: 'gap-6',
            cursor: 'bg-tokens-primary-2',
            tab: 'px-0 h-12',
            tabContent: 'group-data-[selected=true]:text-tokens-primary-2 font-medium'
          }}
        >
          <Tab
            key='find'
            title={
              <div className='flex items-center gap-2'>
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
                <span>Find</span>
              </div>
            }
          />
          <Tab
            key='browse'
            title={
              <div className='flex items-center gap-2'>
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M4 6h16M4 10h16M4 14h16M4 18h16'
                  />
                </svg>
                <span>Browse</span>
              </div>
            }
          />
        </Tabs>
      </div>

      {/* Content */}
      <div className='flex-1 min-h-0'>
        {activeTab === 'find' ? (
          <FindTab
            onTaskSelect={handleTaskSelect}
            onViewConversation={handleViewConversation}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        ) : (
          <BrowseTab
            onTaskSelect={handleTaskSelect}
            onViewConversation={handleViewConversation}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        )}
      </div>

      {/* Task Modal */}
      <Modal
        isOpen={isTaskModalOpen}
        onClose={handleCloseTaskModal}
        className='h-[95vh] w-[80vw]'
        hideCloseButton={false}
      >
        {selectedTaskId != null && (
          <SuperAdminTaskDisplay
            taskId={selectedTaskId}
            workspaceUuid={selectedWorkspaceUuid || undefined}
            onClose={handleCloseTaskModal}
          />
        )}
      </Modal>

      {/* Conversation Modal */}
      <Modal
        isOpen={isConversationModalOpen}
        onClose={handleCloseConversationModal}
        className='h-[85vh] w-[70vw]'
        hideCloseButton={false}
      >
        {viewingConversationId != null && (
          <SuperAdminConversationDisplay
            conversationId={viewingConversationId}
            title={viewingConversationTitle}
          />
        )}
      </Modal>
    </div>
  )
}

const TasksTab: React.FC = () => {
  return <TasksTabContent />
}

export default TasksTab
